// One place that turns a configurable base + brandKey into URLs, so every
// consumer (web apps, emails, the SDK) builds the same paths. The base is empty
// for same-origin local serving (`/shared/…`, `/brands/<brandKey>/…`) and an
// absolute CDN origin in deployed environments (`https://assets.coras.io`). The
// on-disk package tree mirrors these paths exactly, so deploy is a structural sync.
//
// Paths are keyed by `brandKey`, never by `distributorId`. A distributorId names
// a tenant of the booking API; a brandKey names a presentation. One tenant may
// present under several brands (e.g. `pegasus` and `pegasus-tr` share a UUID), so
// only the brandKey uniquely addresses a brand's bytes.

import { FIELDS, type BrandConfig } from "@coras-io/brand-tokens";

/** Canonical top-level paths in the static tree (mirrored on the CDN). */
export const STATIC_PATHS = {
  /** Distributor-agnostic assets: the SDK `assetsUrl` points here. */
  shared: "shared",
  /** Per-brand folder root, addressed by `brandKey`. */
  brands: "brands",
} as const;

/**
 * A brandKey is a path segment on a public CDN, so it is constrained rather than
 * escaped: a slug, never a traversal or a scheme.
 */
const BRAND_KEY = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/** Whether `key` is a well-formed brandKey (lowercase slug, 1-64 chars). */
export function isBrandKey(key: string): boolean {
  return BRAND_KEY.test(key);
}

function assertBrandKey(key: string): string {
  if (!isBrandKey(key)) throw new Error(`Invalid brandKey "${key}".`);
  return key;
}

function join(base: string, ...segments: string[]): string {
  const path = segments
    .flatMap((s) => s.split("/"))
    .map((s) => s.trim())
    .filter(Boolean)
    .join("/");
  const root = base.replace(/\/+$/g, "");
  return `${root}/${path}`;
}

/** A per-brand asset URL: `<base>/brands/<brandKey>/<relativePath>`. */
export function brandAssetUrl(
  base: string,
  brandKey: string,
  relativePath: string,
): string {
  return join(
    base,
    STATIC_PATHS.brands,
    assertBrandKey(brandKey),
    relativePath,
  );
}

/** A shared asset URL: `<base>/shared/<relativePath>`. */
export function sharedAssetUrl(base: string, relativePath: string): string {
  return join(base, STATIC_PATHS.shared, relativePath);
}

/** A brand's `brand.json` URL: `<base>/brands/<brandKey>/brand.json`. */
export function brandConfigUrl(base: string, brandKey: string): string {
  return join(
    base,
    STATIC_PATHS.brands,
    assertBrandKey(brandKey),
    "brand.json",
  );
}

function isAbsolute(path: string): boolean {
  // A full URL the consumer should not rewrite. Store paths are root-relative
  // (`/assets/…`) and DO get resolved; only real schemes pass through, so a
  // self-hosting distributor can still point logo/fonts at its own https URL.
  return (
    /^https?:\/\//i.test(path) || path.startsWith("//") || /^data:/i.test(path)
  );
}

const ASSET_KEYS = FIELDS.filter((f) => f.kind === "asset").map((f) => f.key);

/**
 * Resolve a brand's relative asset paths (every logo, `fontFaces[].url`)
 * against the base + brandKey so the result can be handed straight to the SDK or
 * the email renderer. Absolute (https/data) values pass through unchanged.
 */
export function resolveBrandAssets(
  brand: BrandConfig,
  base: string,
  brandKey: string,
): BrandConfig {
  const resolve = (path: string) =>
    isAbsolute(path) ? path : brandAssetUrl(base, brandKey, path);
  const out: BrandConfig = { ...brand };
  for (const key of ASSET_KEYS) {
    const value = brand[key];
    if (value) out[key] = resolve(value);
  }
  if (brand.fontFaces) {
    out.fontFaces = brand.fontFaces.map((f) => ({ ...f, url: resolve(f.url) }));
  }
  return out;
}
